import { useState, useEffect } from 'react';
import './ColourControl.scss';
import { ColorPicker } from 'primereact/colorpicker';
import InfoIcon from '../../../components/infoIcon/InfoIcon';

const ColourControl = ({ label, infoText, colourKey, whiteLabelSettings, setWhiteLabelSettings, disabled }) => {
	const [colourType, setColourType] = useState('hex'); // Default mode is HEX

	// Converts RGB object {r, g, b} to HEX string "#RRGGBB"
	const rgbToHex = ({ r, g, b }) =>
		`#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;

	// Converts HEX string "#RRGGBB" to RGB object {r, g, b}
	const hexToRgb = (hex) => {
		hex = hex.replace(/^#/, '');
		if (hex.length === 3) {
			hex = hex.split('').map(x => x + x).join('');
		}
		const bigint = parseInt(hex, 16);
		return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
	};

	// Get the current color value for the color picker and inputs
	const getPickerValue = () => {
		const color = whiteLabelSettings[colourKey] || '#FFFFFF';
		return colourType === 'hex' ? color : hexToRgb(color);
	};

	// Handle color changes from the color picker
	const handleColorChange = (e) => {
		const newColor = e.value;
		const hexColor = colourType === 'hex' ? `#${newColor.replace(/^#/, '')}` : rgbToHex(newColor);

		setWhiteLabelSettings((prevSettings) => ({
			...prevSettings,
			[colourKey]: hexColor, // Always store HEX
		}));
	};

	// Handle HEX input changes
	const handleHexInputChange = (e) => {
		let newHex = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6); // Allow only hex characters
		const formattedHex = newHex ? `#${newHex}` : '#FFFFFF';

		setWhiteLabelSettings((prevSettings) => ({
			...prevSettings,
			[colourKey]: formattedHex, // Always store as HEX
		}));
	};

	// Handle RGB input changes
	const handleRgbInputChange = (e, channel) => {
		const value = Math.min(255, Math.max(0, parseInt(e.target.value) || 0)); // Ensure 0-255 range
		const currentRgb = getPickerValue();
		const updatedRgb = { ...currentRgb, [channel]: value };

		setWhiteLabelSettings((prevSettings) => ({
			...prevSettings,
			[colourKey]: rgbToHex(updatedRgb), // Always store HEX
		}));
	};

	// Toggle between HEX and RGB modes
	const toggleColorType = () => {
		setColourType((prevType) => (prevType === 'hex' ? 'rgb' : 'hex'));
	};

	// Convert current value when switching modes
	useEffect(() => {
		const currentColor = whiteLabelSettings[colourKey] || '#FFFFFF';
		const convertedColor = colourType === 'hex' ? currentColor : hexToRgb(currentColor);

		setWhiteLabelSettings((prevSettings) => ({
			...prevSettings,
			[colourKey]: colourType === 'hex' ? `#${convertedColor.replace(/^#/, '')}` : rgbToHex(convertedColor),
		}));
	}, [colourType, colourKey]);

	return (
		<div className="colour-control">
			<h5>{label} <InfoIcon text={infoText} /></h5>
			<div className="colour-control-picker">
				<ColorPicker value={getPickerValue()} onChange={handleColorChange} format={colourType} disabled={disabled} />
			</div>

			<div className="colour-control-numerical">
				{colourType === 'hex' ? (
					<input
						type="text"
						value={whiteLabelSettings[colourKey] || '#FFFFFF'}
						onChange={handleHexInputChange}
						placeholder="#000000"
						disabled={disabled}
					/>
				) : (
					<div className="rgb-inputs">
						<label>R <input type="number" min="0" max="255" value={getPickerValue().r} onChange={(e) => handleRgbInputChange(e, 'r')} /></label>
						<label>G <input type="number" min="0" max="255" value={getPickerValue().g} onChange={(e) => handleRgbInputChange(e, 'g')} /></label>
						<label>B <input type="number" min="0" max="255" value={getPickerValue().b} onChange={(e) => handleRgbInputChange(e, 'b')} /></label>
					</div>
				)}
			</div>

			<div className={`colour-control-type ${disabled ? 'disabled' : ''}`}>
				<div onClick={toggleColorType}>
					<p>{colourType === 'hex' ? 'Switch to RGB' : 'Switch to HEX'}</p>
				</div>
			</div>
		</div>
	);
};

export default ColourControl;
